import { zodResolver } from "@hookform/resolvers/zod";
import { PageProps } from "gatsby";
import React, { useState } from "react";
import { FieldError, UseFormRegister, useForm } from "react-hook-form";
import { z } from "zod";
import { ABTestVariants, SupportedLocale, makePriceMap } from ".";
import { COUNTRY_FOR_LOCALE, LANGUAGE_FOR_LOCALE } from "../../../config";
import { Button } from "../../shared/Button";
import { Img } from "../../shared/Img";
import { languageSwitch } from "../../shared/Languages";
import { Section } from "../../shared/Section";
import { useAdConversionTracking } from "../../shared/tracking/adConversionTracking";
import { useTracking } from "../../shared/tracking/trackUser";

export const ZAPIER_URL_EXPANSION_NOTIFY = "https://hooks.zapier.com/hooks/catch/16193805/2zi6b1b/";

const schema = z.object({
    name: z.string().min(1),
    // phoneNumber: z.string().min(10),
    email: z.string().email(),
    light: z.boolean(),
    pro: z.boolean(),
    commercial: z.boolean(),
    onboarding: z.enum(["self", "supported"]).optional(),
});

type FormData = z.infer<typeof schema>;

export const Waitlist = (props: PageProps & { locale: SupportedLocale; variants: ABTestVariants }) => {
    const lang = LANGUAGE_FOR_LOCALE[props.locale];
    const prices = makePriceMap(props);

    const { track } = useTracking();

    const { trackLeadConversion: trackConversion, trackEnhancedConversion } = useAdConversionTracking();

    const [submitError, setSubmitError] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            light: false,
            pro: false,
            commercial: false,
        },
    });

    const onSubmit = (d: FormData) => {
        const url = new URL(ZAPIER_URL_EXPANSION_NOTIFY);

        // Send data
        const data = {
            ...d,
            locale: props.locale,
            language: lang,
            price: props.variants.price,

            onboarding: d.onboarding,
            selectedPricing: JSON.stringify(prices),
            timestamp: new Date().toISOString(),
        };

        for (const [k, v] of Object.entries(data)) {
            url.searchParams.set(k, v?.toString() ?? "");
        }

        void fetch(url.toString(), { method: "GET" })
            .then((response) => response.json())
            .catch((err) => {
                console.error("Could not send to zapier");
                console.error(err);
            });

        track({
            name: "Expansion Signup",
            type: "Conversion",
            properties: {
                ...data,
            },
        });
        // track conversion to ads
        trackConversion();
        trackEnhancedConversion({
            email: d.email,
            country: COUNTRY_FOR_LOCALE[props.locale],
        });
        setSubmitSuccess(true);
    };

    form.watch();

    return (
        <Section relative={false} outerClassName="pt-56 pb-52" id="waitlist">
            <div className="absolute left-0 top-0 w-full h-full -z-20">
                <Img
                    alt=""
                    size="xxl"
                    className="w-full h-full object-cover"
                    src="/pages/de-de/kristaps-ungurs-U4-p28pPH20-unsplash.jpg"
                />
            </div>
            <div className="text-d-5xl font-bold text-white">
                {languageSwitch(lang, {
                    // Interested?
                    ET: "Huvitatud?",
                    LT: "Domina?",
                    LV: "Interesē?",
                    ES: "Inscríbase en la lista de espera",
                    TR: "Bekleme listesine katılın",
                    CZ: "Připojte se k čekací listině",
                    EL: "Εγγραφείτε στη λίστα αναμονής",
                    FR: "S'inscrire sur la liste d'attente",
                    NL: "Ga naar de wachtlijst",
                    EN: "Interested?",
                })}
            </div>
            <div className="text-d-5xl font-bold mb-8 text-white/80">
                {languageSwitch(lang, {
                    // Secure your early-bird discount now.
                    ET: "Kindlustage oma varajase broneeringu soodustus nüüd.",
                    LT: "Užsitikrinkite išankstinę nuolaidą jau dabar.",
                    LV: "Jau tagad nodrošiniet sev atlaidi par agrīnu reģistrēšanos.",
                    ES: "y asegúrese su descuento por reserva anticipada.",
                    TR: "ve erken rezervasyon indiriminizi güvence altına alın.",
                    CZ: "a zajistěte si slevu za včasné přihlášení.",
                    EL: "και εξασφαλίστε την έκπτωση προεγγραφής πριν την επίσημη κυκλοφορία.",
                    FR: "et bénéficiez d'une réduction pour les inscriptions anticipées.",
                    NL: "en verzeker je van vroegboekkorting.",
                    EN: "Secure your early-bird discount now.",
                })}
            </div>
            <div className="bg-gray-50 p-10">
                {!submitSuccess && (
                    <div className="flex flex-col">
                        <div className="font-bold text-3xl">
                            {languageSwitch(lang, {
                                // We're launching May 1st, 2025.
                                ET: "Me käivitame 1. mail 2025.",
                                LT: "Startuojame 2025 m. gegužės 1 d.",
                                LV: "Mēs sākam darbu 2025. gada 1. maijā.",
                                ES: "Lanzaremos el 1 de mayo de 2025.",
                                TR: "Lansmanı 1 Mayıs 2025'te yapıyoruz.",
                                CZ: "Spouštíme 1. května 2025.",
                                EL: "Ξεκινάμε την 1η Μαΐου 2025.",
                                FR: "Le lancement aura lieu le 1er mai 2025.",
                                NL: "We starten op 1 mei 2025.",
                                EN: "We're launching May 1st, 2025.",
                            })}
                        </div>
                        <div className="font-semibold text-3xl text-gray-500">
                            {languageSwitch(lang, {
                                // Get 30% of your first year
                                ET: "Saate 30% oma esimesest aastast",
                                LT: "Gaukite 30 % pirmųjų metų",
                                LV: "Iegūstiet 30 % no pirmā gada",
                                ES: "Consiga el 30% de descuento el primer año",
                                TR: "İlk yılınızın %30'unu alın",
                                CZ: "Získejte 30 % z ceny za první rok",
                                EL: "Αποκτήστε έκπτωση 30% για το πρώτο έτος",
                                FR: "Bénéficiez de 30% de réduction sur votre première année",
                                NL: "Krijg 30% van je eerste jaar",
                                EN: "Get 30% of your first year",
                            })}
                            {prices.onboarding.price === 0 &&
                                languageSwitch(lang, {
                                    // + no setup fees.
                                    ET: " + ei ole sisseseadmistasu.",
                                    LT: " + jokių diegimo mokesčių.",
                                    LV: " + bez uzstādīšanas maksas.",
                                    ES: " + sin gastos de apertura.",
                                    TR: " + kurulum ücreti yok.",
                                    CZ: " + žádné poplatky za zřízení.",
                                    EL: " + χωρίς έξοδα εγκατάστασης.",
                                    FR: " + pas de frais de mise en service.",
                                    NL: " + geen setupkosten.",
                                    EN: " + no setup fees.",
                                })}
                        </div>
                        <div className="lg:flex space-y-4 lg:space-y-0 mt-4">
                            <div className="lg:max-w-[300px] w-full">
                                <div className="font-medium text-sm">
                                    {languageSwitch(lang, {
                                        // Name
                                        ET: "Nimi",
                                        LT: "Vardas",
                                        LV: "Nosaukums",
                                        ES: "Nombre",
                                        TR: "İsim",
                                        CZ: "Název",
                                        EL: "Όνομα",
                                        FR: "Nom",
                                        NL: "Naam",
                                        EN: "Name",
                                    })}
                                </div>
                                <BasicFormField
                                    className="rounded-full lg:rounded-r-none px-3 py-1 border w-full lg:-ml-[1px] focus-within:z-10 relative"
                                    register={form.register}
                                    error={form.formState.errors.name}
                                    name="name"
                                    type="string"
                                    placeholder={languageSwitch(lang, {
                                        ET: "Nimi,",
                                        LT: "Vardas,",
                                        LV: "Vārds,",
                                        ES: "Nombre",
                                        TR: "Name",
                                        CZ: "Name",
                                        EL: "Όνομα",
                                        FR: "Name",
                                        NL: "Name",
                                        EN: "Name",
                                    })}
                                />
                            </div>
                            <div className="lg:max-w-[300px] w-full">
                                <div className="font-medium text-sm">
                                    {languageSwitch(lang, {
                                        // E-Mail,
                                        ET: "E-mail",
                                        LT: "E-mail",
                                        LV: "E-mail",
                                        ES: "E-mail",
                                        TR: "E-mail",
                                        CZ: "E-mail",
                                        EL: "email",
                                        FR: "E-mail",
                                        NL: "E-mail",
                                        EN: "E-Mail",
                                    })}
                                </div>
                                <BasicFormField
                                    className="rounded-full lg:rounded-l-none px-3 py-1 border w-full lg:-ml-[1px] focus-within:z-10 relative"
                                    register={form.register}
                                    error={form.formState.errors.email}
                                    name="email"
                                    type="email"
                                    placeholder={languageSwitch(lang, {
                                        ET: "E-mail",
                                        LT: "E-mail",
                                        LV: "E-mail",
                                        ES: "E-mail",
                                        TR: "E-mail",
                                        CZ: "E-mail",
                                        EL: "email",
                                        FR: "E-mail",
                                        NL: "E-mail",
                                        EN: "E-Mail",
                                    })}
                                />
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="font-medium text-sm">
                                {languageSwitch(lang, {
                                    // I'm interested in
                                    ET: "Ma olen huvitatud",
                                    LT: "Mane domina",
                                    LV: "Mani interesē",
                                    ES: "Estoy interesado en",
                                    TR: "Ben ilgileniyorum.",
                                    CZ: "Mám zájem o",
                                    EL: "Ενδιαφέρομαι για",
                                    FR: "Je suis intéressé par",
                                    NL: "Ik ben geïnteresseerd in",
                                    EN: "I'm interested in",
                                })}
                            </div>
                            <div className="mt-2 flex items-start gap-1.5 text-sm lg:text-md">
                                <input
                                    id="light"
                                    type="checkbox"
                                    className="mt-[4px] lg:mt-[3px]"
                                    {...form.register("light")}
                                />
                                <label htmlFor="light" className="font-medium">
                                    {prices.light.title}
                                    <span className="font-normal ml-2 text-gray-500">
                                        {prices.light.headline} &middot; {prices.light.description}
                                    </span>
                                </label>
                            </div>
                            <div className="mt-2 flex items-start gap-1.5 text-sm lg:text-md">
                                <input
                                    id="pro"
                                    type="checkbox"
                                    className="mt-[4px] lg:mt-[3px]"
                                    {...form.register("pro")}
                                />
                                <label htmlFor="pro" className="font-medium">
                                    {prices.pro.title}
                                    <span className="font-normal ml-2 text-gray-500">
                                        {prices.pro.headline} &middot; {prices.pro.description}
                                    </span>
                                </label>
                            </div>
                            {props.locale !== "FR-CA" && props.locale !== "EN-CA" && (
                                <div className="mt-2 flex items-start gap-1.5 text-sm lg:text-md">
                                    <input
                                        id="commercial"
                                        type="checkbox"
                                        className="mt-[4px] lg:mt-[3px]"
                                        {...form.register("commercial")}
                                    />
                                    <label htmlFor="commercial" className="font-medium">
                                        {prices.commercial.title}
                                        <span className="font-normal ml-2 text-gray-500">
                                            {prices.commercial.headline} &middot; {prices.commercial.description}
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                        <div className="mt-4">
                            <div className="font-medium text-sm">
                                {" "}
                                {languageSwitch(lang, {
                                    EN: "I would like a personal onboarding",
                                    ET: "Mul on vaja personaalset sisseelamisseanssi.",
                                    LT: "Man reikia asmeninės įvadinės sesijos.",
                                    LV: "Man vajadzīga personīga ievadsesija.",
                                    ES: "Necesitas una sesión de onboarding personalizada.",
                                    TR: "Kişisel bir giriş oturumuna ihtiyacım var.",
                                    CZ: "Potřebuji osobní úvodní školení.",
                                    EL: "Χρειάζομαι μια προσωπική συνεδρία εισαγωγής.",
                                    FR: "J’ai besoin d’une session d’onboarding personnalisée.",
                                    NL: "Ik heb een persoonlijke onboarding-sessie nodig.",
                                })}
                                <span className="font-semibold">
                                    {" "}
                                    (
                                    {prices.onboarding.price.toLocaleString(props.locale, {
                                        style: "currency",
                                        minimumFractionDigits: 0,
                                        // TODO: add currency for all locales
                                        currency: languageSwitch(lang, {
                                            EN: "CAD",
                                            ET: "EUR",
                                            LT: "EUR",
                                            LV: "EUR",
                                            ES: "MXN",
                                            TR: "EUR",
                                            CZ: "CZK",
                                            EL: "EUR",
                                            FR: "CAD",
                                            NL: "EUR",
                                        }),
                                    })}
                                    )
                                </span>
                                {prices.onboarding.price === 0 && (
                                    <span className="font-normal ml-2 text-gray-500">
                                        {languageSwitch(lang, {
                                            EN: "Included for early-bird signups",
                                            ET: "Kaasa arvatud varajase registreerumise korral",
                                            LT: "Įtraukta į išankstinių registracijų sąrašą",
                                            LV: "Iekļauts agrīnās reģistrācijas gadījumā",
                                            ES: "Incluido para inscripciones anticipadas",
                                            TR: "Erken kayıt yaptıranlar için dahildir",
                                            CZ: "Zahrnuto pro včasné přihlášení",
                                            EL: "Προσφορά για τις προεγγραφές early-bird πριν την επίσημη κυκλοφορία",
                                            FR: "Inclus pour les inscriptions anticipées",
                                            NL: "Inbegrepen voor vroege vogels",
                                        })}
                                    </span>
                                )}
                            </div>

                            <>
                                <div className="mt-2 flex items-start gap-1.5 text-sm lg:text-md">
                                    <input
                                        {...form.register("onboarding")}
                                        value="supported"
                                        id="supported"
                                        type="radio"
                                        className="mt-[4px] lg:mt-[3px]"
                                    />
                                    <label htmlFor="supported" className="font-medium">
                                        {languageSwitch(lang, {
                                            EN: "Yes",
                                            ET: "Jah",
                                            LT: "Taip",
                                            LV: "Yes",
                                            ES: "Sí",
                                            TR: "Evet",
                                            CZ: "Ano",
                                            EL: "Ναι",
                                            FR: "Oui",
                                            NL: "Ja",
                                        })}
                                    </label>
                                </div>
                                <div className="mt-2 flex items-start gap-1.5 text-sm lg:text-md">
                                    <input
                                        {...form.register("onboarding")}
                                        value="self"
                                        id="self"
                                        type="radio"
                                        className="mt-[4px] lg:mt-[3px]"
                                    />
                                    <label htmlFor="self" className="font-medium">
                                        {languageSwitch(lang, {
                                            EN: "No",
                                            ET: "Ei",
                                            LT: "Ne",
                                            LV: "Nē",
                                            ES: "No",
                                            TR: "Hayır",
                                            CZ: "Ne",
                                            EL: "Όχι",
                                            FR: "Non",
                                            NL: "Geen",
                                        })}
                                    </label>
                                </div>
                            </>
                        </div>

                        {submitError && (
                            <div className="text-red-500 text-xs">An error occured when submitting the form.</div>
                        )}
                        <Button
                            className="bg-black hover:bg-zinc-800 text-white w-fit mt-2"
                            children={languageSwitch(lang, {
                                // Join the waitlist
                                ET: "Liitu ootenimekirjaga",
                                LT: "Prisijunkite prie laukiančiųjų sąrašo",
                                LV: "Pievienojies gaidīšanas sarakstam",
                                ES: "Inscribirse en la lista de espera",
                                TR: "Bekleme listesine katılın",
                                CZ: "Připojte se k čekací listině",
                                EL: "Εγγραφείτε στη λίστα αναμονής",
                                FR: "S'inscrire sur la liste d'attente",
                                NL: "Ga naar de wachtlijst",
                                EN: "Join the waitlist",
                            })}
                            onClick={form.handleSubmit(onSubmit, (e) => {
                                setSubmitError(true);
                                console.log("error", e);
                            })}
                        />
                    </div>
                )}

                {submitSuccess && (
                    <div>
                        <div className="text-d-3xl font-semibold my-20">
                            <Img src="/images/logo.svg" alt="" size="lg" className="w-20 mb-2" />
                            {languageSwitch(lang, {
                                ET: "Täname teid huvi eest meie tarkvara vastu!",
                                LT: "Dėkojame, kad domitės mūsų programine įranga!",
                                LV: "Paldies par jūsu interesi par mūsu programmatūru!",
                                ES: "Gracias por su interés en nuestro software.",
                                TR: "Yazılımımıza gösterdiğiniz ilgi için teşekkür ederiz!",
                                CZ: "Děkujeme vám za váš zájem o náš software!",
                                EL: "Σας ευχαριστούμε για το ενδιαφέρον σας για το λογισμικό μας!",
                                FR: "Merci de l'intérêt que vous portez à notre logiciel !",
                                NL: "Bedankt voor je interesse in onze software!",
                                EN: "Thank you for your interest in our software!",
                            })}
                            <div className="text-lg text-gray-700">
                                {languageSwitch(lang, {
                                    ET: "Võtame teiega peagi ühendust.",
                                    LT: "Netrukus su jumis susisieksime.",
                                    LV: "Mēs drīz ar jums sazināsimies.",
                                    ES: "Pronto nos pondremos en contacto con usted.",
                                    TR: "Yakında sizinle iletişime geçeceğiz.",
                                    CZ: "Brzy se vám ozveme.",
                                    EL: "Θα επικοινωνήσουμε μαζί σας σύντομα.",
                                    FR: "Nous vous contacterons bientôt.",
                                    NL: "We nemen snel contact met je op.",
                                    EN: "We will be in touch with you soon.",
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* <div className="bg-white">
                {["a", "b", "c", "d", "e", "f"].map((m) => {
                    const price = makePriceMap({
                        locale: props.locale,
                        variants: {
                            onboardingFee: "a",
                            price: m as "a" | "b" | "c" | "d" | "e" | "f",
                        },
                    });
                    return (
                        <>
                            <div className="text-red-300">{m}</div>
                            <div>{price.light.headline}, {price.light.description}</div>
                            <div>{price.pro.headline}, {price.pro.description}</div>
                            <div>{price.commercial.headline}, {price.commercial.description}</div>
                        </>
                    );
                })}
            </div> */}
        </Section>
    );
};

const BasicFormField = ({
    type,
    placeholder,
    name,
    register,
    error,
    valueAsNumber,
    className,
}: {
    type: string;
    placeholder?: string;
    name: keyof FormData;
    register: UseFormRegister<FormData>;
    error: FieldError | undefined;
    valueAsNumber?: boolean;
    className?: string;
}) => (
    <>
        <input className={className} type={type} placeholder={placeholder} {...register(name, { valueAsNumber })} />
        {error && <span className="text-red-500 text-sm">{error.message}</span>}
    </>
);
